import type {
  CancelarSuscripcionInput,
  IniciarPagoInput,
  PortalDeFacturacionInput,
  ReembolsarPedidoInput,
} from '@qr/shared/billing';
import type { Role } from '@qr/shared/types';
import type {
  ChangePlanInput,
  CreateQrCodeInput,
  InviteMemberInput,
  UpdateMemberRoleInput,
  UpdateQrCodeInput,
} from '@qr/shared/schemas';
import { FunctionsError, httpsCallable } from 'firebase/functions';
import { functions } from './firebase.js';

/**
 * Llamadas a las funciones del servidor, con tipos.
 *
 * Toda escritura con consecuencias pasa por aqui y no por el SDK de Firestore:
 * las reglas de seguridad prohiben expresamente escribir codigos, miembros y
 * planes desde el cliente, porque hay comprobaciones (limites del plan,
 * reserva de slug, analisis del destino) que solo pueden hacerse en servidor.
 */

function callable<TInput, TOutput>(name: string) {
  const fn = httpsCallable<TInput, TOutput>(functions, name);
  return async (input: TInput): Promise<TOutput> => {
    const result = await fn(input);
    return result.data;
  };
}

export const api = {
  createQrCode: callable<
    CreateQrCodeInput,
    // `shortHost` lo decide el servidor: es el dominio que queda sellado en el
    // codigo, y con el que hay que dibujar el QR para que lo impreso coincida.
    { codeId: string; slug: string; shortHost: string }
  >('createQrCode'),
  updateQrCode: callable<UpdateQrCodeInput, { ok: true }>('updateQrCode'),
  /**
   * Creacion masiva. Devuelve el resultado LINEA A LINEA, no un si o un no:
   * una lista de ochenta mesas con una direccion mal escrita no debe fallar
   * entera, y quien la envio necesita saber cual corregir.
   */
  createQrCodesBulk: callable<
    { orgId: string; codes: { name: string; targetUrl: string }[] },
    {
      shortHost: string;
      creados: number;
      resultados: {
        name: string;
        ok: boolean;
        slug?: string;
        codeId?: string;
        error?: string;
      }[];
    }
  >('createQrCodesBulk'),
  deleteQrCode: callable<{ codeId: string }, { ok: true }>('deleteQrCode'),
  checkSlugAvailability: callable<
    { slug: string },
    { available: boolean; reason: string | null }
  >('checkSlugAvailability'),

  /**
   * `correoEnviado` dice si el aviso llego a salir. No es un error que sea
   * false —el producto funciono meses sin proveedor de correo—, pero cambia
   * por completo lo que hay que decirle a quien invita: o "ya se lo hemos
   * mandado" o "pasale tu este enlace".
   */
  inviteMember: callable<
    InviteMemberInput,
    { inviteId: string; acceptUrl: string; expiresAt: string; correoEnviado: boolean }
  >('inviteMember'),
  acceptInvite: callable<{ inviteId: string; token: string }, { orgId: string }>(
    'acceptInvite',
  ),
  /**
   * Las invitaciones que siguen vivas. Va por funcion y no por Firestore
   * porque las reglas prohiben leer `invites`: ahi esta el hash del token.
   */
  listInvites: callable<
    { orgId: string },
    {
      invitaciones: {
        inviteId: string;
        email: string;
        role: Role;
        expiresAt: string;
        caducada: boolean;
      }[];
    }
  >('listInvites'),
  /**
   * Rehace el enlace. No existe "volver a ver el anterior": del token solo se
   * guarda el hash, asi que reenviar crea uno nuevo y mata el viejo.
   */
  resendInvite: callable<
    { orgId: string; inviteId: string },
    { acceptUrl: string; expiresAt: string; correoEnviado: boolean }
  >('resendInvite'),
  revokeInvite: callable<{ orgId: string; inviteId: string }, { ok: true }>(
    'revokeInvite',
  ),
  updateMemberRole: callable<UpdateMemberRoleInput, { ok: true }>('updateMemberRole'),
  removeMember: callable<{ orgId: string; uid: string }, { ok: true }>('removeMember'),
  leaveOrg: callable<{ orgId: string }, { ok: true }>('leaveOrg'),
  createOrg: callable<{ name: string }, { orgId: string }>('createOrg'),

  /**
   * Carpetas. Borrar una NO borra sus codigos: se quedan sin carpeta. Por eso
   * `deleteFolder` devuelve cuantos libero, para poder decirlo.
   */
  createFolder: callable<{ orgId: string; name: string }, { folderId: string }>(
    'createFolder',
  ),
  renameFolder: callable<
    { orgId: string; folderId: string; name: string },
    { ok: true }
  >('renameFolder'),
  deleteFolder: callable<
    { orgId: string; folderId: string },
    { ok: true; codigosLiberados: number }
  >('deleteFolder'),

  /**
   * Autorizacion para subir un archivo. Devuelve una URL que solo sirve para
   * subir ese archivo a esa ruta y caduca en diez minutos; las credenciales
   * del almacenamiento no llegan nunca al navegador.
   */
  createUploadUrl: callable<
    { orgId: string; contentType: string; size: number; purpose?: 'file' | 'logo' },
    {
      uploadUrl: string;
      key: string;
      publicUrl: string;
      headers: Record<string, string>;
    }
  >('createUploadUrl'),
  deleteUploadedFile: callable<{ orgId: string; key: string }, { ok: boolean }>(
    'deleteUploadedFile',
  ),

  /**
   * Contratar un plan. Devuelve la direccion donde se paga, en el dominio del
   * proveedor: por el panel no pasa ningun dato de tarjeta.
   *
   * Que esto responda bien NO significa que el plan ya este activo. El plan lo
   * aplica el webhook cuando el cobro esta confirmado, asi que al volver del
   * pago puede tardar unos segundos en verse.
   */
  iniciarPago: callable<IniciarPagoInput, { url: string; plan: string }>('iniciarPago'),
  /**
   * Portal del cliente: tarjeta, facturas y cambio de plan. El enlace va
   * firmado y caduca, asi que se pide uno nuevo cada vez y no se guarda.
   */
  portalDeFacturacion: callable<PortalDeFacturacionInput, { url: string }>(
    'portalDeFacturacion',
  ),
  /** Baja al final del periodo ya pagado; el plan sigue hasta esa fecha. */
  cancelarPlan: callable<
    CancelarSuscripcionInput,
    { ok: true; terminaEl: string | null }
  >('cancelarPlan'),

  changePlan: callable<ChangePlanInput, { ok: true; plan: string }>('changePlan'),
  /** Devolucion de un cobro. Solo superadmin: mueve dinero hacia atras. */
  reembolsar: callable<ReembolsarPedidoInput, { ok: true }>('reembolsar'),
  /**
   * Vuelve a leer la suscripcion del proveedor. Es la salida para cuando un
   * webhook se perdio y una cuenta pago sin ver su plan subir.
   */
  sincronizarSuscripcion: callable<
    { orgId: string; subscriptionId: string },
    { ok: true; estado: string; plan: string }
  >('sincronizarSuscripcion'),
  /** Mantenimiento puntual, solo superadmin. */
  backfillMemberships: callable<
    Record<string, never>,
    { revisados: number; corregidos: number }
  >('backfillMemberships'),
  cleanupOrphanOrgs: callable<
    Record<string, never>,
    { revisadas: number; borradas: number; perfilesBorrados: number }
  >('cleanupOrphanOrgs'),

  setOrgStatus: callable<
    { orgId: string; status: 'active' | 'suspended'; reason: string },
    { ok: true }
  >('setOrgStatus'),
};

/**
 * Convierte el error de una funcion invocable en un mensaje para la interfaz.
 *
 * Los mensajes del servidor ya estan escritos para leerse; lo que no se puede
 * hacer es mostrar el error crudo, que incluye detalles internos.
 */
export function messageFromError(error: unknown): string {
  if (error instanceof FunctionsError) {
    switch (error.code) {
      case 'functions/unauthenticated':
        return 'Tu sesion ha caducado. Vuelve a entrar.';
      case 'functions/permission-denied':
        return error.message || 'No tienes permiso para esta accion.';
      case 'functions/resource-exhausted':
        return error.message || 'Has alcanzado el limite de tu plan.';
      case 'functions/already-exists':
        return error.message || 'Ese elemento ya existe.';
      case 'functions/invalid-argument':
        return error.message || 'Revisa los datos introducidos.';
      case 'functions/failed-precondition':
        return error.message || 'La peticion no se pudo verificar.';
      default:
        return 'Algo ha ido mal. Intentalo de nuevo en unos segundos.';
    }
  }
  /*
   * Errores de Firebase Auth. Sus mensajes vienen en ingles y con el codigo
   * entre parentesis ("Firebase: Error (auth/...)"), que no significa nada
   * para quien esta intentando entrar.
   */
  const code = (error as { code?: string } | null)?.code;
  if (typeof code === 'string' && code.startsWith('auth/')) {
    switch (code) {
      case 'auth/operation-not-allowed':
        return 'Ese metodo de acceso no esta disponible todavia. Entra con tu correo.';
      case 'auth/account-exists-with-different-credential':
        return 'Ya hay una cuenta con ese correo creada de otra forma. Entra con tu correo y el enlace.';
      case 'auth/unauthorized-domain':
        return 'Este sitio no esta autorizado para iniciar sesion. Avisanos.';
      case 'auth/network-request-failed':
        return 'No hemos podido conectar. Revisa tu conexion e intentalo otra vez.';
      case 'auth/too-many-requests':
        return 'Demasiados intentos seguidos. Espera un momento y vuelve a probar.';
      case 'auth/invalid-email':
        return 'Revisa la direccion: parece que le falta algo.';
      default:
        return 'No hemos podido completar el acceso. Intentalo de nuevo.';
    }
  }

  if (error instanceof Error) return error.message;
  return 'Algo ha ido mal. Intentalo de nuevo en unos segundos.';
}
